
import React, { useEffect, useRef } from 'react';
import { ShieldOff, DollarSign, TrendingUp, Package } from 'lucide-react';

const problems = [
  {
    icon: <ShieldOff className="w-6 h-6" />,
    title: "In-House Data Lacks Credibility",
    description: "Consumers don't trust data collected by brands themselves"
  },
  {
    icon: <DollarSign className="w-6 h-6" />,
    title: "Influencer Bias is Obvious",
    description: "Paid partnerships feel inauthentic when rewards come directly from brands"
  },
  {
    icon: <TrendingUp className="w-6 h-6" />,
    title: "Influencer Costs Are Skyrocketing",
    description: "Traditional influencer contracts drain budgets without guaranteed ROI"
  },
  {
    icon: <Package className="w-6 h-6" />,
    title: "No Scalable Verification",
    description: "Current methods can't deliver verifiable data at the scale you need"
  }
];

const ProblemSection: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elements = entry.target.querySelectorAll('.animate-on-scroll');
            elements.forEach((el, i) => {
              setTimeout(() => {
                el.classList.add('visible');
              }, i * 150); // stagger the animations
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section ref={sectionRef} className="section-padding bg-white">
      <div className="container">
        <div className="text-center max-w-3xl mx-auto mb-16 animate-on-scroll">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            The Trust Gap in Health Marketing is Costing You Sales
          </h2>
          <p className="text-neutral-700 text-lg">
            Health-conscious consumers are skeptical of brand claims. Without verified data, your marketing falls flat.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {problems.map((problem, index) => (
            <div 
              key={index} 
              className="bg-white rounded-lg p-6 border border-neutral-200 shadow-soft animate-on-scroll flex flex-col"
            >
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
                <div className="text-red-500">
                  {problem.icon}
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">{problem.title}</h3>
              <p className="text-neutral-700">{problem.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;
